import React, { useMemo } from 'react';
import bannersModule, { BannerTypes } from '@gin-betting/banners';
import { BannerZoneLocation } from '@gin-betting/constants';
import { GQL } from '@gin-betting/core';

import { Carousel } from 'components';
import { AnalyticAttrs, AnalyticAttrsCtx, Category } from 'services/analytics';
import CMSBanner from '../CMSBanner';
import ColorfulBanner from '../ColorfulBanner';
import useColorfulBanners from './useColorfulBanners';

import './colorfulBanners.scss';

const {
    hocs: { withAvailableZoneByAttributes },
} = bannersModule;

interface OwnProps {
    zoneLocation: BannerTypes.BannerZoneLocation;
}

type Props = OwnProps & BannerTypes.WithAvailableZoneProps;

const ColorfulBanners: React.FC<Props> = ({ zoneId, zoneLocation }) => {
    const { filteredBanners, interval } = useColorfulBanners({
        zoneId,
    });
    const analyticAttrs = useMemo(
        () => getAnalyticAttrsByZoneLocation(zoneLocation),
        [zoneLocation]
    );

    if (!filteredBanners.length) return null;

    const renderBanners = () =>
        filteredBanners.map(banner => {
            switch (banner.type) {
                case GQL.BannerType.cms_banner: {
                    return (
                        <AnalyticAttrsCtx.Provider
                            value={analyticAttrs}
                            key={`${banner.type}-${banner.id}`}
                        >
                            <CMSBanner bannerId={banner.id} />
                        </AnalyticAttrsCtx.Provider>
                    );
                }

                default: {
                    return (
                        <ColorfulBanner
                            key={`${banner.type}-${banner.id}`}
                            banner={banner}
                        />
                    );
                }
            }
        });

    return (
        <div styleName="container">
            <Carousel hasControls={true} delay={interval} direction="left">
                {renderBanners()}
            </Carousel>
        </div>
    );
};

function getAnalyticAttrsByZoneLocation(
    zoneLocation: BannerZoneLocation
): Partial<AnalyticAttrs> {
    switch (zoneLocation) {
        case BannerZoneLocation.TOP_SLIDER:
            return {
                'data-category': Category.BannerTopSlider,
            };

        case BannerZoneLocation.RIGHT:
            return {
                'data-category': Category.BannerRight,
            };
        case BannerZoneLocation.RIGHT_SLIDER:
            return {
                'data-category': Category.BannerRightSlider,
            };
        default:
            return {};
    }
}

export default withAvailableZoneByAttributes(ColorfulBanners);
